import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextInventory } from '../Inventory';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Category } from '../../../../models/category.model';
import { Feature } from '../../../../models/feature.model';
import { FeatureDetail } from '../../../../models/feature.detail.model';
import { modalShow } from '../../../../utils/modal.util';
import { legendValid } from '../../../../utils/legend.data.util';

import AppInventoryFormAccountingAccount from './InventoryFormAccountingAccount';
import AppModalCategoryCreate from '../../../../components/modal/ModalCategoryCreate';
import AppLegend from '../../../../components/element/Legend';
import AppModalSettingsCreateFeature from '../../../../components/modal/ModalSettingsCreateFeature';

export interface AppInventoryFormDetailProps {
  inventory: string,
  action: string,
  category: {value: string, valid: boolean, object: any},
  accounts: {value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}},
  list: Feature[],
  features?: {value: FeatureDetail[], valid: boolean[]},
  variantDefault?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  variants?: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]},
  setCategory: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setAccounts: Dispatch<SetStateAction<{value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}}>>,
  setList: Dispatch<SetStateAction<Feature[]>>,
  setFeatures?: Dispatch<SetStateAction<{value: FeatureDetail[], valid: boolean[]}>>,
  setVariantDefault?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>,
  setVariants?: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]}>>
};

const AppInventoryFormDetail: React.FunctionComponent<AppInventoryFormDetailProps> = ({inventory, action, category, accounts, list, features, variantDefault, variants, setCategory, setAccounts, setList, setFeatures, setVariantDefault, setVariants}) => {
  const {companyForUser, settingsForCompany, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, setCategoriesForCompany, loadSettingsForCompany} = UseOutletContextInventory()
  const {lang} = StateLanguage()

  const handleChangeCategory = (item: Category) => {
    setCategory({value: item.id, valid: true, object: item})
    setAccounts({value: {expense: item.accounts.expense, vat: item.accounts.vat, income: item.accounts.income}, valid: {expense: true, vat: true, income: true}})

    legendValid('legend-category-required')
    legendValid('legend-account-expense-required')
    legendValid('legend-account-vat-required')
    legendValid('legend-account-income-required')
  }

  const handleSelectFeature = (item: Feature) => {
    setList([...list, item])
    if (features && setFeatures) {
      setFeatures({value: [...features.value, {id_feature: item.id, name: item.name, value: ''}], valid: [...features.valid, false]})
    }
    if (variantDefault && variants && setVariantDefault && setVariants) {
      if (variants.value.length > 1 && variants.valid.length > 1) {
        setVariants({value: variants.value.map((variant) => ({...variant, features: [...variant.features, {id_feature: item.id, name: item.name, value: ''}]})), valid: variants.valid.map((variant) => ({...variant, features: [...variant.features, false]}))})
      } else {
        setVariantDefault({value: {...variantDefault.value, features: [...variantDefault.value.features , {id_feature: item.id, name: item.name, value: ''}]}, valid: {...variantDefault.valid, features: [...variantDefault.valid.features, false]}})
      }
    }
  }

  const executeRemoveFeature = (item: string) => {
    setList(list.filter((temp_item) => (temp_item.id !== item)))
    if (features && setFeatures) {
      let index = features.value.findIndex((temp_item) => (temp_item.id_feature === item))
      setFeatures({value: features.value.filter((_, temp_index) => (temp_index !== index)), valid: features.valid.filter((_, temp_index) => (temp_index !== index))})
    }
    if (variantDefault && variants && setVariantDefault && setVariants) {
      if (variants.value.length > 1 && variants.valid.length > 1) {
        setVariants({value: variants.value.map(variant => ({...variant, features: variant.features.filter(feature => feature.id_feature !== item)})), valid: variants.valid.map((variant, index) => ({...variant, features: variant.features.filter((_, temp_index) => variants.value[index].features[temp_index]?.id_feature !== item)}))})
      } else {
        let index = variantDefault.value.features.findIndex((temp_item) => (temp_item.id_feature === item))
        setVariantDefault({value: {...variantDefault.value, features: variantDefault.value.features.filter((_, temp_index) => (temp_index !== index))}, valid: {...variantDefault.valid, features: variantDefault.valid.features.filter((_, temp_index) => (temp_index !== index))}})
      }
    }
  }

  const executeShowModalSettingsCreateFeature = () => {
    modalShow('modal-settings-create-feature')
  }

  const executeShowModalCategoryCreate = () => {
    modalShow('modal-category-create')
  }

  function featureList(): Feature[] {
    if (features && setFeatures){
      return settingsForCompany?.features.filter((item) => (item.type === inventory)).filter((item) => (!features.value.some((sub_item) => (sub_item.id_feature === item.id)))) ?? []
    } if (variantDefault && variants && setVariantDefault && setVariants) {
      if (variants.value.length > 1 && variants.valid.length > 1) {
        return settingsForCompany?.features.filter((item) => (item.type === inventory)).filter((item) => (!variants.value.some((sub_item) => (sub_item.features.some(((other_item) => (other_item.id_feature === item.id))))))) ?? []
      } else {
        return settingsForCompany?.features.filter((item) => (item.type === inventory)).filter((item) => (!variantDefault.value.features.some((sub_item) => (sub_item.id_feature === item.id)))) ?? []
      }
    } else {
      return []
    }
  }

  function featureSelectList(): FeatureDetail[] {
    if (features && setFeatures) {
      return features.value
    } if (variantDefault && variants && setVariantDefault && setVariants) {
      if (variants.value.length > 1 && variants.valid.length > 1) {
        return variants.value.flatMap((item) => (item.features)).filter((item, index, array) => (index === array.findIndex((sub_item) => (sub_item.id_feature === item.id_feature))))
      } else {
        return variantDefault.value.features
      }
    } else {
      return []
    }
  }

  return (
    <>
      { companyForUser && categoriesForCompany && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany &&
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h3>{lang.labels[`${inventory}Details` as keyof typeof lang.labels]}</h3>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="d-flex flex-wrap justify-content-between align-items-end mb-2">
              <label className="form-label">{lang.labels.features}</label>
              <button className="btn btn-light-primary btn-sm" type="button" onClick={executeShowModalSettingsCreateFeature}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-3 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.addFeature}
                </div>
              </button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                <span className="selection">
                  <span className="select2-selection select2-selection--single form-select">
                    <span className="select2-selection__rendered" role="textbox">
                      {lang.labels.selectOption}
                    </span>
                  </span>
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                <span className="select2-dropdown select2-dropdown--below">
                  <span className="select2-results">
                    <ul className="select2-results__options" role="listbox">
                      <li className={`select2-results__option select2-results__option--selectable ${featureList().length > 0 && "d-none"}`}>
                        <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                      </li>
                      { featureList().map (( (item, index) => { return (
                        <li key={index} className="select2-results__option select2-results__option--selectable">
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleSelectFeature(item)}>{item.name}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </span>
                </span>
              </Dropdown.Menu>
            </Dropdown>
            { featureSelectList().length > 0 &&
              <div className="rti--container mt-3">
                { featureSelectList().map (( (item, index) => { return (
                  <div key={index} className="rti--tag tag-cls bg-dark bg-opacity-10 fs-7 my-1">
                    <span>{item.name}</span>
                    <button type="button" aria-label="remove" onClick={() => executeRemoveFeature(item.id_feature)}>✕</button>
                  </div>
                )}))}
              </div>
            }
            <div className="separator my-5"></div>
            <div className="d-flex flex-wrap justify-content-between align-items-end mb-2">
              <label className="form-label required">{lang.labels.category}</label>
              <button className="btn btn-light-primary btn-sm" type="button" onClick={executeShowModalCategoryCreate}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-3 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.addCategory}
                </div>
              </button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                <span className="selection">
                  <span className="select2-selection select2-selection--single form-select">
                    <span className="select2-selection__rendered" role="textbox">
                      {category.valid ? category.object.display_name : lang.labels.selectOption}
                    </span>
                  </span>
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                <span className="select2-dropdown select2-dropdown--below">
                  <span className="select2-results">
                    <ul className="select2-results__options" role="listbox">
                      <li className={`select2-results__option select2-results__option--selectable ${categoriesForCompany.length > 0 && "d-none"}`}>
                        <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                      </li>
                      { categoriesForCompany.map (( (item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === category.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === category.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeCategory(item)}>{item.display_name}</Dropdown.Item>
                        </li>
                      )}))}
                    </ul>
                  </span>
                </span>
              </Dropdown.Menu>
            </Dropdown>
            <div className={`form-text text-justify ${action === "update" && "d-none"}`}>{lang.labels[`add${inventory.charAt(0).toUpperCase()}${inventory.slice(1)}ToCategory` as keyof typeof lang.labels]}</div>
            <AppLegend attribute={{check: category.valid, name: "category", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
            <div className="separator my-5"></div>
            <AppInventoryFormAccountingAccount accounts={accounts} setAccounts={setAccounts}></AppInventoryFormAccountingAccount>
          </div>
        </div>
      }
      <AppModalSettingsCreateFeature companyForUser={companyForUser} type={inventory} loadSettingsForCompany={loadSettingsForCompany} features={features} variants={variants} variantDefault={variantDefault} setFeatures={setFeatures} setVariants={setVariants} setVariantDefault={setVariantDefault}></AppModalSettingsCreateFeature>
      <AppModalCategoryCreate companyForUser={companyForUser} categoriesForCompany={categoriesForCompany} accountingExpenseAccountsForCompany={accountingExpenseAccountsForCompany} accountingVatAccountsForCompany={accountingVatAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} category={category} setCategoriesForCompany={setCategoriesForCompany} setCategory={setCategory} setInventoryAccounts={setAccounts}></AppModalCategoryCreate>
    </>
  )
};

export default AppInventoryFormDetail;
